import React, { useState, useEffect } from 'react';
import * as technicianService from '../../../services/technicianService.js';
import { Copy, Check } from '@phosphor-icons/react';
import { getTaskStatusBadgeStyles, getTaskStatusLabel } from '../../../constants/taskColors.js';

/**
 * Componente de tarjeta individual de tarea para el dashboard de técnico
 * @param {Object} task - Objeto de tarea
 * @param {number} taskTimer - Tiempo transcurrido en segundos (opcional)
 * @param {boolean} showLocationLinks - Mostrar links de ubicación
 * @param {Function} onAction - Callback para acciones (id, action)
 * @param {Function} onOpenGoogleMaps - Callback para abrir Google Maps
 * @param {Function} onOpenAddressModal - Callback para abrir modal de dirección
 * @param {Function} onOpenAddressModal - Callback para abrir modal de dirección
 * @param {Function} onOpenCancelModal - Callback para abrir modal de cancelación
 * @param {Function} onOpenFormModal - Callback para abrir modal de formulario (finalization)
 * @param {Function} onOpenGeneralFormModal - Callback para abrir modal de formulario general
 * @param {Function} onOpenFinalizationForm - Callback para abrir formulario de finalización
 * @param {boolean} hasActiveTask - Si hay otra tarea activa
 * @param {boolean} hasPendingForm - Si hay formulario pendiente
 * @param {boolean} generalFormAvailable - Si se indica, evita consultar si existe formulario general
 *   (lo usa la tarea de ejemplo del recorrido guiado, que no existe en el backend)
 */
function TaskCard({
    task,
    taskNumber = null,
    taskTimer = 0,
    showLocationLinks = false,
    onAction,
    onOpenGoogleMaps,
    onOpenAddressModal,
    onOpenCancelModal,
    onOpenFormModal,
    onOpenGeneralFormModal,
    onOpenFinalizationForm,
    hasActiveTask = false,
    hasPendingForm = false,
    generalFormAvailable
}) {
    const getPriorityColor = technicianService.getPriorityColor;
    const formatTime = technicianService.formatTime;

    // Estado para verificar si existe formulario general
    const [checkedGeneralForm, setHasGeneralForm] = useState(null); // null = no verificado aún
    const hasGeneralForm = generalFormAvailable ?? checkedGeneralForm;
    const [copied, setCopied] = useState(false);
    const [copiedSecondary, setCopiedSecondary] = useState(false);

    const handleCopyPhone = async (phoneNumber, isSecondary = false) => {
        if (!phoneNumber) return;

        let success = false;

        // Método 1: Clipboard API moderno (HTTPS/localhost)
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(phoneNumber);
                success = true;
            } catch (err) {
                console.warn('Clipboard API failed, trying fallback:', err);
            }
        }

        // Método 2: Fallback usando textarea + execCommand (HTTP)
        if (!success) {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = phoneNumber;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);

                textarea.select();
                textarea.setSelectionRange(0, phoneNumber.length);

                success = document.execCommand('copy');
                document.body.removeChild(textarea);
            } catch (err) {
                console.error('Fallback copy failed:', err);
            }
        }

        if (success) {
            if (isSecondary) {
                setCopiedSecondary(true);
                setTimeout(() => setCopiedSecondary(false), 2000);
            } else {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        }
    };

    // Verificar si existe formulario general cuando la tarea está en estado "iniciada"
    useEffect(() => {
        const checkGeneralForm = async () => {
            if (task.status === 'iniciada' && generalFormAvailable === undefined) {
                try {
                    const forms = await technicianService.fetchFormsByTaskType(
                        task.taskTypeId,
                        'general'
                    );
                    setHasGeneralForm(forms && forms.length > 0);
                } catch (error) {
                    console.error('Error checking general form:', error);
                    setHasGeneralForm(false);
                }
            }
        };

        checkGeneralForm();
    }, [task.status, task.taskTypeId, generalFormAvailable]); // Re-verificar si cambia el estado o tipo de tarea

    return (
        <div data-tour="tech-task-card" className="bg-surface rounded-lg shadow-md border border-line p-3 sm:p-4 lg:p-6 h-full flex flex-col">
            {/* Header de la tarea */}
            <div className="bg-surface-2 p-4 flex items-center h-full">
                <div className="flex items-center justify-between gap-3 w-full">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        {taskNumber !== null && (
                            <div className="flex h-7 w-7 min-w-[1.75rem] items-center justify-center rounded-full border border-line bg-surface text-xs font-semibold text-content aspect-square">
                                {taskNumber}
                            </div>
                        )}
                        <h3 className="text-lg font-semibold text-content break-words leading-snug">
                            {task.taskTypeName || 'Tarea sin tipo'}
                        </h3>
                    </div>

                    <span
                        data-tour="tech-task-status"
                        className="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap"
                        style={getTaskStatusBadgeStyles(task.status)}
                    >
                        {getTaskStatusLabel(task.status)}
                    </span>
                </div>
            </div>

            {/* Contenido de la tarea */}
            <div className="p-4 space-y-3 flex-1 flex flex-col">
                {/* Información del cliente y prioridad */}
                <div data-tour="tech-task-client" className="space-y-3">
                <div className="flex items-start space-x-3">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-content">{task.client?.name || 'Sin cliente asignado'}</p>
                        {task.client?.phone && (
                            <div>
                                <div className="flex items-center gap-2 pt-2">
                                    <p className="text-content  font-medium text-sm">{task.client?.phone}</p>
                                    {copied ? (
                                        <Check size={20} className="text-success" />
                                    ) : (
                                        <Copy size={20} className="text-content  cursor-pointer hover:text-content-muted transition-colors" onClick={() => handleCopyPhone(task.client?.phone)} />
                                    )}
                                </div>
                            </div>
                        )}
                        {!task.client?.phone && (
                            <p className="text-sm font-medium text-content">Teléfono: Sin teléfono asignado</p>
                        )}
                        {task.client?.secondaryPhone && (
                            <div className="flex items-center gap-2 pt-2">
                                <p className="text-sm font-medium text-content-muted e">{task.client?.secondaryPhone}</p>
                                {copiedSecondary ? (
                                    <Check size={20} className="text-success" />
                                ) : (
                                    <Copy size={20} className="text-content-muted  cursor-pointer hover:text-content transition-colors" onClick={() => handleCopyPhone(task.client?.secondaryPhone, true)} />
                                )}
                            </div>
                        )}
                    </div>
                </div>


                <div className="flex items-center">
                    <p className="text-sm text-content">
                        <span className={`text-xs font-medium px-3 py-1 rounded-full border ${getPriorityColor(task.priority)}`}>
                            {"Prioridad " + task.priority}
                        </span>
                    </p>
                </div>
                </div>

                {/* Contador de tiempo para tareas iniciadas */}
                {(task.status === 'iniciada' || task.status === 'relevada') && (
                    <div data-tour="tech-task-timer" className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber/12 rounded-full flex items-center justify-center">
                            <span className="text-sm">⏱️</span>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-content">
                                Tiempo transcurrido: <span className="font-mono font-bold text-accent-text">{formatTime(taskTimer)}</span>
                            </p>
                        </div>
                    </div>
                )}

                {/* Links de ubicación expandibles */}

                <div className="w-full">
                    <button
                        data-tour="tech-task-details"
                        onClick={() => onOpenAddressModal(task)}
                        className="w-full bg-surface-2 hover:bg-line text-content px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                        <span>Detalles</span>
                    </button>
                </div>

                {/* Botones de acción */}
                <div className="flex flex-wrap gap-1 sm:gap-2 lg:gap-3 mt-auto pt-3 sm:pt-4">
                    {/* Botón "En camino" */}
                    {(task.status === 'pendiente' && !hasActiveTask) && (
                        <button
                            data-tour="tech-action-en-camino"
                            onClick={() => onAction(task.id, 'en-camino')}
                            className="w-full bg-amber hover:bg-amber-hover text-on-amber px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                        >
                            <span>En camino</span>
                        </button>
                    )}

                    {/* Botón "Iniciar" */}
                    {(task.status === 'llegando' || task.status === 'en_camino') && (
                        <button
                            data-tour="tech-action-start"
                            onClick={() => onAction(task.id, 'start')}
                            className="flex-1 bg-amber hover:bg-amber-hover text-on-amber px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                        >
                            <span>Iniciar</span>
                        </button>
                    )}

                    {/* Botón "Formulario General" - solo si está iniciada y tiene formulario */}
                    {task.status === 'iniciada' && hasGeneralForm === true && (
                        <button
                            data-tour="tech-action-form"
                            onClick={() => onOpenGeneralFormModal(task)}
                            className="flex-1 bg-amber hover:bg-amber-hover text-on-amber px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                        >
                            <span>Formulario</span>
                        </button>
                    )}

                    {/* Indicador de verificación */}
                    {task.status === 'iniciada' && hasGeneralForm === null && (
                        <div className="flex-1 flex items-center justify-center px-4 py-3">
                            <span className="text-sm text-content-muted">Verificando...</span>
                        </div>
                    )}

                    {/* Botón "Finalizar" - si está relevada O si está iniciada sin formulario general */}
                    {((task.status === 'iniciada' && hasGeneralForm === false) || task.status === 'relevada') && (
                        <button
                            data-tour="tech-action-finish"
                            onClick={() => onOpenFinalizationForm(task)}
                            className="flex-1 bg-success hover:bg-success/90 text-white px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                            disabled={hasPendingForm}
                        >
                            <span>Finalizar</span>
                        </button>
                    )}

                    {/* Botón "Cancelar" - Habilitado para tareas en_camino, iniciada o relevada */}
                    {(task.status === 'en_camino' || task.status === 'iniciada' || task.status === 'relevada') && (
                        <button
                            data-tour="tech-action-cancel"
                            onClick={() => onOpenCancelModal(task)}
                            className="flex-1 bg-error hover:bg-error/90 text-white px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                            disabled={hasPendingForm}
                        >
                            <span>Cancelar</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default TaskCard;
